import { Injectable } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { Constants } from 'app/constants/constants';
import { FilterService, WidgetQueryOptions } from 'app/modules/collection/services/filter.service';
import { ActionItemCacheService } from 'app/modules/dashboard/services/action-item-cache.service';
import { ActionPlanCacheService } from 'app/modules/dashboard/services/action-plan-cache.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comparison, Dashboard, WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  ActionItem,
  ActionPlan,
  ActionPlanService,
} from 'app/providers/storage-api/actionplan.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import { Report, ReportService } from 'app/providers/storage-api/report.service';
import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { getStatusTranslation } from 'app/utils/action-plan.utils';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { ActionItemModel } from '../action-item-editor/action-item-editor.component';
import { copyWidgetGroupFilters } from '../widget/widget.component';
import { WidgetImpactOptions } from '../widget-impact/widget-impact.component';
import { getBigNumberMetadataResultFromReport } from '../../services/chart-report/chart-report-bignumber/chart-report-bignumber-metadata';
import {
  ActionItemDto,
  ActionPlanDialogOptions,
  ActionPlanModel,
} from './action-plan-dialog.component';

export class ActionPlanCreationInput {
  actionPlan: ActionPlanModel;
  widgetDataSource: WidgetDataSource;
  dashboard: Dashboard;
  payload: WidgetImpactOptions;
  widgetQueryOptions: WidgetQueryOptions;
  widgetId: string;
  widgetGroup?: WidgetGroup;
  comparison?: Comparison;

  constructor(actionPlan: ActionPlanModel, actionPlanDialogOptions: ActionPlanDialogOptions) {
    this.actionPlan = actionPlan;
    this.widgetDataSource = actionPlanDialogOptions.widgetDataSource;
    this.dashboard = actionPlanDialogOptions.dashboard;
    this.payload = actionPlanDialogOptions.payload;
    this.widgetQueryOptions = actionPlanDialogOptions.widgetQueryOptions;
    this.widgetId = actionPlanDialogOptions.widgetId;
    this.widgetGroup = actionPlanDialogOptions.widgetGroup;
    this.comparison = actionPlanDialogOptions.comparison;
  }
}

@EnableClassTelemetry()
@Injectable()
export class ActionPlanDialogService {
  constructor(
    private actionPlanService: ActionPlanService,
    private groupService: GroupService,
    private projectService: ProjectService,
    private actionPlanCacheService: ActionPlanCacheService,
    private actionItemCacheService: ActionItemCacheService,
    private reportService: ReportService,
    private filterService: FilterService,
    private timezoneService: TimezoneService,
    private alertService: AlertService,
    private authService: AuthService
  ) {}

  async createActionPlan(input: ActionPlanCreationInput): Promise<void> {
    try {
      const actionPlan = await this.createActionPlanObject(input);

      const createdActionPlan = await this.actionPlanService.createActionPlan(
        this.groupService.getSelectedGroupId(),
        actionPlan
      );

      this.actionPlanCacheService.add(createdActionPlan);

      this.alertService.showToast(translate('action-plan-dialog.success-create-action-plan'));
    } catch (error) {
      this.alertService.showErrorToast(
        translate('action-plan-dialog.failed-create-action-plan'),
        error
      );

      throw error;
    }
  }

  async updateActionPlan(actionPlan: ActionPlan, model: ActionPlanModel): Promise<void> {
    try {
      ActionPlanDialogService.mapActionPlanModel(actionPlan, model);

      const updatedActionPlan = await this.actionPlanService.updateActionPlan(
        this.groupService.getSelectedGroupId(),
        actionPlan
      );

      this.actionPlanCacheService.update(updatedActionPlan);

      this.alertService.showToast(translate('action-plan-dialog.success-save-action-plan'));
    } catch (error) {
      this.alertService.showErrorToast(
        translate('action-plan-dialog.failed-save-action-plan'),
        error
      );

      throw error;
    }
  }

  async saveActionItems(actionPlanId: string, items: ActionItemDto[]): Promise<void> {
    try {
      const newItems = items.filter((i) => i.isNew && i.state !== 'deleted');
      if (newItems.length) {
        await this.createActionItems(actionPlanId, newItems);
      }

      const existingItems = items.filter((i) => !i.isNew);

      const updatedItems = existingItems.filter((i) => i.state === 'updated');
      if (updatedItems.length) {
        await this.updateActionItems(actionPlanId, updatedItems);
      }

      const deletedItems = existingItems.filter((i) => i.state === 'deleted');
      if (deletedItems.length) {
        await this.deleteActionItems(actionPlanId, deletedItems);
      }

      this.alertService.showToast(translate('action-plan-dialog.success-save-action-items'));
    } catch (error) {
      this.alertService.showErrorToast(
        translate('action-plan-dialog.failed-save-action-items'),
        error
      );

      throw error;
    }
  }

  async getCurrentUserFullName(): Promise<string> {
    const session = await this.authService.getSession();

    return `${session.user.first_name} ${session.user.last_name}`;
  }

  static createItemDto(model: ActionItemModel, itemType: string, itemValue: string): ActionItemDto {
    const actionItem = ActionPlanService.createActionItemObject({
      name: model.name,
      dueDate: Utils.getDateIsoString(model.dueDate),
      assignee: model.assignee,
      notes: model.notes,
      itemType,
      itemValue,
    });

    return {
      ...actionItem,
      state: 'original',
      statusText: getStatusTranslation(actionItem.status),
      isNew: true,
    };
  }

  static updateItemDto(dto: ActionItemDto, model: ActionItemModel): void {
    dto.name = model.name;
    dto.assignee_user_name = model.assignee;
    dto.due_date = Utils.getDateIsoString(model.dueDate);
    dto.status = model.status;
    dto.notes = model.notes;
    dto.state = 'updated';
    dto.statusText = getStatusTranslation(model.status);
  }

  static mapItemsToDtos(actionItems: ActionItem[]): ActionItemDto[] {
    return actionItems.map((i) => ({
      ...i,
      state: 'original',
      statusText: getStatusTranslation(i.status),
    }));
  }

  private async createActionPlanObject(input: ActionPlanCreationInput): Promise<ActionPlan> {
    const [project, metric] = await Promise.all([
      this.projectService.getProjectById(input.dashboard.project_id, false),
      this.calculateMetric(input),
    ]);

    const actionPlan = ActionPlanService.createActionPlanObject({
      notes: input.actionPlan.notes,
      dueDate: Utils.getDateIsoString(input.actionPlan.dueDate),
      calculationId: input.widgetDataSource.id,
      collectionName: input.widgetDataSource.collectionName,
      collectionDisplayName: input.widgetDataSource.displayName,
      driverColumn: input.payload.ratingField,
      driverName: FieldService.getFieldLongName(input.payload.ratingField),
      breakdown: Constants.TWTRA_ALL_BREAKDOWN,
      originalMetric: metric,
      targetMetric: metric,
      projectId: project.id,
      projectName: project.name,
      dashboardId: input.dashboard.id,
      scoringStrategy: input.payload.scoringStrategy,
      filters: JSON.stringify(input.payload.filters),
      comparisonFilters: input.comparison?.filters.length
        ? JSON.stringify(input.comparison.filters[0])
        : undefined,
      widgetGroupFilters: input.widgetGroup
        ? JSON.stringify(copyWidgetGroupFilters(input.widgetGroup, input.widgetId))
        : undefined,
      weightField: input.payload.weightField,
    });

    return actionPlan;
  }

  private async calculateMetric(input: ActionPlanCreationInput): Promise<number> {
    const params = {
      analysisName: input.widgetDataSource.analysisId,
      timezone: this.timezoneService.timezone,
      boxSize: input.payload.boxSize,
      scoringStrategy: input.payload.scoringStrategy,
      boxDirection: input.payload.boxDirection,
      scoreRange: input.payload.scoreRange,
      customField: input.payload.ratingField,
    };

    const report = (await this.reportService.postReport(
      input.widgetDataSource.collectionName,
      input.widgetDataSource.groupId,
      'documentNumericMetadata',
      params,
      undefined,
      'json',
      this.filterService.getQueryForDocuments(input.widgetQueryOptions, false, true),
      undefined,
      undefined,
      input.payload.projectId
    )) as Report<{ [key: string]: any }>;

    const bigNumberMetadataResult = getBigNumberMetadataResultFromReport(input.payload, report);

    return bigNumberMetadataResult.result;
  }

  private static mapActionPlanModel(actionPlan: ActionPlan, model: ActionPlanModel): void {
    actionPlan.creator_user_name = model.creator;
    actionPlan.due_date = Utils.getDateIsoString(model.dueDate);
    actionPlan.notes = model.notes;
    actionPlan.status = model.status;
  }

  private async createActionItems(actionPlanId: string, items: ActionItemDto[]): Promise<void> {
    const createdActionItems = await Promise.all(
      items.map((i) => this.actionPlanService.createActionItem(actionPlanId, i))
    );

    this.actionItemCacheService.set(actionPlanId, ...createdActionItems);
  }

  private async updateActionItems(actionPlanId: string, items: ActionItemDto[]): Promise<void> {
    const updatedActionItems = await Promise.all(
      items.map((i) => this.actionPlanService.updateActionItem(actionPlanId, i))
    );

    this.actionItemCacheService.set(actionPlanId, ...updatedActionItems);
  }

  private async deleteActionItems(actionPlanId: string, items: ActionItemDto[]): Promise<void> {
    await Promise.all(
      items.map((i) => this.actionPlanService.deleteActionItem(actionPlanId, i.item_id))
    );

    this.actionItemCacheService.delete(actionPlanId, ...items.map((i) => i.item_id));
  }
}
